import Vue from "vue";
import { io } from "socket.io-client";

import { EventBus } from "@/eventBus";

const SOCKET_HOST = typeof process.env.VUE_APP_SOCKET_HOST !== "undefined"
  ? process.env.VUE_APP_SOCKET_HOST
  : "https://unicall.io";
const SOCKET_PATH = typeof process.env.VUE_APP_SOCKET_PATH !== "undefined"
  ? process.env.VUE_APP_SOCKET_PATH
  : "/socket/socket.io/";

class ApiSocketService {
  constructor() {
    this.socket = null;
    this.root = null;
    this.store = null;
    this.eventBusHandlers = null;
    this.isInitialized = false;
  }

  init({ root, store }) {
    this.root = root;
    this.store = store;

    if (this.isInitialized) {
      return;
    }

    this.registerEventBusHandlers();
    this.isInitialized = true;
  }

  setConnected(connected) {
    if (!this.store) return;

    this.store.commit("SET_API_SOCKET_CONNECTED", connected);
  }

  currentState() {
    let userState;

    switch (+window.sessionStorage.getItem("state")) {
      case 0:
        userState = "online";
        break;
      case 1:
        userState = "is-ready";
        break;
      case 2:
        userState = "training";
        break;
      default:
        userState = "online";
    }

    if (this.store && this.store.state.order.order) {
      userState = "opened-order";
    }

    return userState;
  }

  emitStatusUpdate(status, data = {}) {
    if (!this.socket || !this.socket.connected) return;

    this.socket.emit("statusUpdate", status, data);
  }

  emitCabinetLog(data = {}) {
    if (!this.socket || !this.socket.connected) return;

    this.socket.emit("cabinet-log", data);
  }

  connect() {
    if (this.socket || !Vue.auth.isAuthenticated()) {
      return;
    }

    const token = Vue.auth.getToken();
    const user = Vue.auth.getUser();
    if (!token || !user || !user.username) {
      return;
    }

    this.socket = io(SOCKET_HOST, {
      path: SOCKET_PATH,
      query: {
        token,
        username: user.username,
        state: this.currentState(),
      },
      transports: ["websocket"],
    });

    this.socket.on("connect", () => {
      this.setConnected(true);
      console.log("Connected to WebSocket server");
    });

    this.socket.on("disconnect", (reason) => {
      this.setConnected(false);
      console.log(`Disconnected from WebSocket server: ${reason}`);
    });

    this.socket.on("clean old session", () => {
      this.handleSessionConflict();
    });

    this.socket.on("initialTime", (data) => {
      console.log("initialTime time:", data);
    });

    this.socket.on("command", (data) => {
      console.log("Command:", data);
      if (data === "reload") {
        window.location.reload();
      }
    });
  }

  disconnect() {
    if (!this.socket) {
      this.setConnected(false);
      return;
    }

    this.socket.off();
    this.socket.disconnect();
    this.socket = null;
    this.setConnected(false);
  }

  syncConnection() {
    if (Vue.auth.isAuthenticated()) {
      this.connect();
      return;
    }

    this.disconnect();
  }

  handleSessionConflict() {
    EventBus.$emit("destroy-app-instance");
    this.disconnect();

    if (this.root && !this.root._isDestroyed) {
      this.root.$destroy();
    }

    document.body.innerHTML = "";
    document.body.textContent =
      "Another opened tab has been detected. Disconnected by server.";
  }

  registerEventBusHandlers() {
    this.eventBusHandlers = {
      openedOrder: () => {
        const orderId = this.store?.state?.order?.order?.order?.id;
        if (!orderId) return;

        this.emitStatusUpdate("opened-order", { orderId });
      },
      changeReady: () => {
        this.emitStatusUpdate(this.currentState());
      },
      savedOrder: () => {
        this.emitStatusUpdate(this.currentState(), {});
      },
      stopCalling: () => {
        const orderId = this.store?.state?.order?.order?.order?.id;
        const data = orderId ? { orderId } : {};
        this.emitStatusUpdate(this.currentState(), data);
      },
      startCalling: () => {
        const orderId = this.store?.state?.order?.order?.order?.id;
        const data = orderId ? { orderId } : {};
        this.emitStatusUpdate("calling", data);
      },
      logoutUser: () => {
        this.disconnect();
      },
      cabinetLog: (data) => {
        this.emitCabinetLog(data);
      },
    };

    EventBus.$on("opened-order", this.eventBusHandlers.openedOrder);
    EventBus.$on("change-ready", this.eventBusHandlers.changeReady);
    EventBus.$on("saved-order", this.eventBusHandlers.savedOrder);
    EventBus.$on("stop-calling", this.eventBusHandlers.stopCalling);
    EventBus.$on("start-calling", this.eventBusHandlers.startCalling);
    EventBus.$on("logout-user", this.eventBusHandlers.logoutUser);
    EventBus.$on("cabinet-log", this.eventBusHandlers.cabinetLog);
  }
}

export default new ApiSocketService();
